import React, { useState } from "react";

const skills = [
	{
		title: "Student Obsession",
		tagline: "Everything we do starts with our students and the WGU mission.",
		header: "bg-primary",
		points: [
			"Designed the Academic Readiness for Nursing (ARN) TEAS-prep experience in Open edX with diagnostics, practice, and Emotional Intelligence content grounded in real student needs.",
			"Iterated on interactive course elements (carousels, comics, calculators, ledger activities) based on where students slowed or got stuck, reducing friction and confusion.",
		],
	},
	{
		title: "Innovation",
		tagline: "Fresh thinking, calculated risks, and the courage to go first.",
		header: "bg-secondary",
		points: [
			"Prototyped, built, and deployed an automated voucher system with an AWS S3-hosted front end, Node.js/Express.js on EC2, and MongoDB—removing manual steps from the TEAS voucher process.",
			"Designed non-traditional interactives in Open edX (virtual comics, responsive image grids, custom carousels) to move beyond passive reading and video.",
			"Experimented with AI-assisted authoring to speed builds while respecting data security and privacy constraints.",
		],
	},
	{
		title: "Learning",
		tagline: "Treating self-improvement as ongoing and benchmarking against the best.",
		header: "bg-accent",
		points: [
			"Pursuing a B.S. in Computer Science while completing advanced WGU assessments in SQL, computer architecture, discrete math, and calculus—feeding that knowledge back into real product work.",
			"Built structured study plans and practice exams (e.g., TEAS/HESI, C952, Calculus I) that informed how diagnostic and practice experiences should feel for students.",
			"Regularly incorporates feedback from accessibility specialists, product partners, and designers to refine patterns and course templates.",
		],
	},
	{
		title: "Inspire & Develop",
		tagline: "Inspiring others to do their best work and actively developing the people around me.",
		header: "bg-primary",
		points: [
			"Mentored colleagues on Open edX workflows, Git branching, and shared CSS/JS pipelines, helping others ship interactive experiences with confidence.",
			"Created internal how-tos, SMARTER goal templates, and design docs that translate abstract ideas into concrete, shippable course components.",
			"Consistently highlights cross-functional contributions (Design, Product Health, Student Comms, Assessment) in docs and presentations.",
		],
	},
	{
		title: "Urgency",
		tagline: "Responding quickly, focusing on what matters, and maintaining momentum.",
		header: "bg-secondary",
		points: [
			"Delivered key Open edX launches—such as Academic Readiness for Nursing and orientation redesign—on tight timelines without sacrificing accessibility or quality.",
			"Rapidly debugged and refactored fragile LMS patterns (e.g., mobile comic bubbles, non-JS carousels, interactive ledgers) so stakeholders could evaluate working prototypes, not just concepts.",
			"Quickly adapted voucher workflows and content so newly enrolled students could access TEAS vouchers with minimal delay.",
		],
	},
	{
		title: "Courage",
		tagline: "Big, bold thinking and backbone — doing what is right, not just what is easy.",
		header: "bg-accent",
		points: [
			"Advocated for student- and accessibility-first decisions in tool evaluations and course patterns, even when that meant more work or pushing back on simpler options.",
			"Challenged legacy manual processes by proposing automation (voucher pipelines, shared CSS libraries, MongoDB data flows) to reduce error and free teams to focus on higher-value work.",
			"Provided candid feedback in RFPs and process reviews so leaders clearly saw both risks and opportunities.",
		],
	},
	{
		title: "Ownership",
		tagline: "Thinking long term and never saying \u201cthat\u2019s not my job.\u201d",
		header: "bg-primary",
		points: [
			"Took end-to-end responsibility for the TEAS voucher experience—from MongoDB integration concepts through the front-end flow and messaging alignment with Student Comms.",
			"Built and maintained shared CSS/JS resources in GitHub and AWS so future courses can reuse components instead of rebuilding from scratch.",
			"Proactively surfaced and owned solutions to pain points (manual vouchers, brittle interactives, confusing authoring vs. production practices in Open edX).",
		],
	},
	{
		title: "Deliver Results",
		tagline: "A high bar for quality and timeliness, with clear, measurable outcomes.",
		header: "bg-secondary",
		highlight: "100% reduction in voucher support tickets",
		points: [
			"Developed and launched an automated voucher system that reduced voucher-related student support tickets by 100%, eliminating manual voucher lookups and assignment errors.",
			"Delivered multiple Open edX courses (ARN, orientation redesign, certificate courses) as scalable, documented products instead of one-off builds.",
			"Established reusable interactive patterns (ledgers, calculators, carousels, image grids) to accelerate future development and keep quality consistent.",
		],
	},
	{
		title: "Integrity",
		tagline: "Doing what is right even when no one is watching.",
		header: "bg-accent",
		highlight: "Academy Summit Integrity Award",
		points: [
			"Recognized at Academy's Summit with an Integrity award for consistently modeling honest communication and principled decision-making.",
			"Set and upheld clear boundaries for AI and data usage in course development to protect student privacy and institutional trust.",
			"Raised accessibility and quality concerns early—even when it required rework—so launches consistently aligned with WGU standards.",
		],
	},
	{
		title: "Earn Trust",
		tagline: "Listening deeply, communicating directly, and following through.",
		header: "bg-primary",
		points: [
			"Built credibility with Design Managers, Product Health, Student Comms, and Assessment partners by consistently delivering on commitments and documenting decisions clearly.",
			"Translated complex technical topics (Open edX authoring vs. production workflows, voucher automation, GitHub-to-AWS CSS pipelines) into accessible explanations for non-technical stakeholders.",
			"Used retrospectives and honest feedback loops on projects (ARN, orientation, RFP/process work) to strengthen relationships and improve how we collaborate.",
		],
	},
];

function LeadershipSkills() {
	const [openIndex, setOpenIndex] = useState(null);

	const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

	return (
		<div className="bg-slate-50 pt-0 pb-16">
			<div className="max-w-5xl mx-auto px-4 md:px-6">
				<h1 className="text-4xl font-bold text-primary mb-2 mt-8 text-center">Leadership Skills</h1>
				<p className="text-center text-gray-400 text-sm mb-10 tracking-wide uppercase">10 principles · click any card to expand</p>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-5">
					{skills.map((skill, i) => {
						const isOpen = openIndex === i;
						const num = String(i + 1).padStart(2, "0");

						return (
							<div
								key={i}
								onClick={() => toggle(i)}
								className={`group cursor-pointer rounded-2xl overflow-hidden shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 ${
									isOpen ? "md:col-span-2" : ""
								}`}
							>
								{/* Card header */}
								<div className={`${skill.header} px-6 pt-5 pb-4 relative`}>
									{/* Large decorative number */}
									<span className="absolute top-3 right-5 text-6xl font-black text-white/10 leading-none select-none">
										{num}
									</span>
									<div className="relative z-10">
										<p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-1">
											Principle {num}
										</p>
										<h3 className="text-white text-xl font-bold leading-tight">{skill.title}</h3>
										<p className="text-white/75 text-sm mt-1 leading-snug">{skill.tagline}</p>
									</div>
								</div>

								{/* Always-visible footer */}
								<div className="bg-white px-6 py-3 flex items-center justify-between border-t border-gray-100">
									{skill.highlight ? (
										<span className="text-xs font-semibold text-accent bg-accent/10 px-2 py-1 rounded-full">
											★ {skill.highlight}
										</span>
									) : (
										<span className="text-xs text-gray-400">{skill.points.length} examples</span>
									)}
									<span className={`text-gray-300 text-sm transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
										▾
									</span>
								</div>

								{/* Expanded content */}
								{isOpen && (
									<div className="bg-white px-6 pb-6 border-t border-gray-100">
										<ul className="space-y-3 mt-2">
											{skill.points.map((point, j) => (
												<li key={j} className="flex gap-3 text-sm text-gray-700 leading-relaxed">
													<span className="text-accent mt-0.5 shrink-0 font-bold">→</span>
													<span>{point}</span>
												</li>
											))}
										</ul>
									</div>
								)}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}

export default LeadershipSkills;
